import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { format } from "date-fns";
import {
  Search, Zap, CalendarIcon, ChevronLeft, ChevronRight, Plus, Loader2, Clock, Gamepad2,
  LayoutGrid, List, Ban, Trash2, CalendarOff, Settings, X, Hourglass, Percent,
} from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_TZ, tzLabel, zonedWallToUtc, zonedParts } from "@/lib/timezones";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow, Modal, Field } from "@/components/admin/ui";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const GRID_START = 8 * 60;
const GRID_END = 24 * 60;
const STEP = 15;
const ROW_H = 18;
const ROWS = (GRID_END - GRID_START) / STEP;
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const STATUS_COLOR = { confirmed: "#00ff9d", active: "#00e5ff", pending: "#ffb020", cancelled: "#ff2e97", completed: "#94a3b8" };
const BLOCK_COLOR = "#ff2e97";
const TOURNAMENT_COLOR = "#ffd23f";

const fmtMin = (m) => {
  const mm = ((m % 1440) + 1440) % 1440;
  const h = Math.floor(mm / 60), min = mm % 60;
  const hr12 = ((h + 11) % 12) + 1;
  return `${hr12}:${min === 0 ? "00" : String(min).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const START_OPTIONS = [];
for (let m = GRID_START; m <= GRID_END - STEP; m += STEP) START_OPTIONS.push(m);
const DURATION_OPTIONS = [];
for (let d = 15; d <= 240; d += 15) DURATION_OPTIONS.push(d);
const HOUR_CHOICES = [];
for (let m = 0; m <= 1440; m += 30) HOUR_CHOICES.push(m);
const fmtDur = (d) => (d < 60 ? `${d} min` : d % 60 === 0 ? `${d / 60} hr` : `${Math.floor(d / 60)} hr ${d % 60} min`);

export default function AdminBookings() {
  const { user } = useAuth();
  const tz = DEFAULT_TZ;

  const [view, setView] = useState("grid");
  const [mode, setMode] = useState("book"); // book | block
  const [bookings, setBookings] = useState([]);
  const [stations, setStations] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [day, setDay] = useState(new Date());

  const [modalOpen, setModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [groupExpiryOpen, setGroupExpiryOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => {}),
      apiClient.get("/admin/stations").then(({ data }) => setStations(data.filter((s) => s.is_active !== false))).catch(() => {}),
      apiClient.get("/business-hours").then(({ data }) => setBusinessHours(data)).catch(() => setBusinessHours([])),
      apiClient.get("/closures").then(({ data }) => setClosures(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sel = { y: day.getFullYear(), monthIndex: day.getMonth(), day: day.getDate() };
  const dayStartUTC = useMemo(() => zonedWallToUtc(sel.y, sel.monthIndex, sel.day, 0, tz), [sel.y, sel.monthIndex, sel.day, tz]);
  const dayEndUTC = useMemo(() => new Date(dayStartUTC.getTime() + 24 * 60 * 60000), [dayStartUTC]);

  // Selected-day schedule
  const weekday = day.getDay();
  const dateStr = format(day, "yyyy-MM-dd");
  const closure = closures.find((c) => c.date === dateStr);
  const dh = businessHours?.find((d) => d.weekday === weekday);
  const closedDay = !!closure || !!dh?.is_closed;
  const openM = dh?.open_minutes ?? 540;
  const closeM = dh?.close_minutes ?? 1320;
  const closedLabel = closure ? `Holiday — ${closure.name}` : dh?.is_closed ? `Closed on ${WEEKDAYS[weekday]}s` : null;

  const blocksByStation = useMemo(() => {
    const map = {};
    stations.forEach((s) => { map[s.id] = []; });
    bookings.forEach((b) => {
      const bStart = new Date(b.start_time), bEnd = new Date(b.end_time);
      if (isNaN(bStart) || isNaN(bEnd)) return;
      if (bEnd <= dayStartUTC || bStart >= dayEndUTC) return;
      const sp = zonedParts(bStart, tz), ep = zonedParts(bEnd, tz);
      let startMin = bStart <= dayStartUTC ? GRID_START : sp.minutes;
      let endMin = bEnd >= dayEndUTC ? GRID_END : ep.minutes;
      startMin = Math.max(GRID_START, startMin);
      endMin = Math.min(GRID_END, endMin);
      if (endMin <= startMin) return;
      (b.station_ids || []).forEach((sid) => {
        if (!map[sid]) return;
        map[sid].push({ ...b, _top: ((startMin - GRID_START) / STEP) * ROW_H, _height: ((endMin - startMin) / STEP) * ROW_H, _startMin: startMin, _endMin: endMin });
      });
    });
    return map;
  }, [bookings, stations, dayStartUTC, dayEndUTC, tz]);

  const onRange = useCallback((stationIds, startMin, endMin, moved) => {
    if (!stationIds.length) return;
    const duration = moved && endMin > startMin ? endMin - startMin : 60;
    const p = { stationIds, startMin, durationMin: Math.min(duration, GRID_END - startMin) };
    if (mode === "block") { setPrefill(p); setBlockOpen(true); }
    else { setPrefill(p); setModalOpen(true); }
  }, [mode]);

  const deleteBooking = async (id) => {
    try { await apiClient.delete(`/admin/bookings/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Could not remove")); }
  };

  const shiftDay = (delta) => setDay((d) => { const nd = new Date(d); nd.setDate(nd.getDate() + delta); return nd; });

  const filtered = bookings.filter((b) => {
    const s = q.toLowerCase();
    return !s || `${b.user_name || ""} ${b.user_email || ""} ${b.station_names || ""}`.toLowerCase().includes(s);
  });

  if (loading) return <Spinner label="Loading bookings" />;

  return (
    <div>
      <AdminHeader
        title="Bookings"
        subtitle={`${bookings.length} total · times in ${tzLabel(tz)}`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setHoursOpen(true)} className="neon-outline-btn px-3 py-2.5 text-xs" data-testid="hours-btn"><Settings className="w-4 h-4" /> Hours</button>
            <button onClick={() => setHolidaysOpen(true)} className="neon-outline-btn px-3 py-2.5 text-xs" data-testid="holidays-btn"><CalendarOff className="w-4 h-4" /> Holidays</button>
            {user?.role === "admin" && (
              <button onClick={() => setGroupExpiryOpen(true)} className="neon-outline-btn px-3 py-2.5 text-xs" data-testid="group-expiry-btn"><Hourglass className="w-4 h-4" /> Group Invites</button>
            )}
            {user?.role === "admin" && (
              <button onClick={() => setTaxOpen(true)} className="neon-outline-btn px-3 py-2.5 text-xs" data-testid="tax-settings-btn"><Percent className="w-4 h-4" /> Tax (HST)</button>
            )}
            <div className="flex rounded-xl overflow-hidden border border-white/10">
              <button onClick={() => setView("grid")} data-testid="view-grid"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${view === "grid" ? "text-black" : "text-slate-300"}`}
                style={{ background: view === "grid" ? "#00e5ff" : "transparent" }}><LayoutGrid className="w-4 h-4" /> Day</button>
              <button onClick={() => setView("list")} data-testid="view-list"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${view === "list" ? "text-black" : "text-slate-300"}`}
                style={{ background: view === "list" ? "#00e5ff" : "transparent" }}><List className="w-4 h-4" /> List</button>
            </div>
          </div>
        }
      />

      {view === "grid" ? (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <button onClick={() => shiftDay(-1)} className="neon-outline-btn px-3 py-2.5" data-testid="prev-day"><ChevronLeft className="w-4 h-4" /></button>
            <Popover>
              <PopoverTrigger asChild>
                <button className="input-cyber flex items-center gap-2 !w-auto px-4" data-testid="day-picker-trigger">
                  <CalendarIcon className="w-4 h-4 text-slate-400" /> {format(day, "EEEE, MMMM d, yyyy")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 glass border-white/10" align="start">
                <Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} initialFocus />
              </PopoverContent>
            </Popover>
            <button onClick={() => shiftDay(1)} className="neon-outline-btn px-3 py-2.5" data-testid="next-day"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setDay(new Date())} className="text-slate-400 hover:text-white text-xs uppercase tracking-widest" data-testid="today-btn">Today</button>

            {/* Book / Block mode toggle */}
            <div className="flex rounded-xl overflow-hidden border border-white/10 ml-auto">
              <button onClick={() => setMode("book")} data-testid="mode-book"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${mode === "book" ? "text-black" : "text-slate-300"}`}
                style={{ background: mode === "book" ? "#00ff9d" : "transparent" }}><Plus className="w-4 h-4" /> Book</button>
              <button onClick={() => setMode("block")} data-testid="mode-block"
                className={`px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${mode === "block" ? "text-black" : "text-slate-300"}`}
                style={{ background: mode === "block" ? BLOCK_COLOR : "transparent" }}><Ban className="w-4 h-4" /> Block</button>
            </div>
            <button onClick={() => { setPrefill(null); (mode === "block" ? setBlockOpen : setModalOpen)(true); }} className="neon-btn px-4 py-2.5" data-testid="new-booking-btn">
              <Plus className="w-4 h-4" /> {mode === "block" ? "New Block" : "New Booking"}
            </button>
          </div>

          {closedLabel && (
            <div className="mb-3 rounded-xl p-3 text-sm flex items-center gap-2" data-testid="grid-closed-banner"
              style={{ background: "rgba(255,46,151,0.12)", border: "1px solid rgba(255,46,151,0.45)", color: "#ff8ac4" }}>
              <CalendarOff className="w-4 h-4" /> <b>{closedLabel}</b> — the lounge is closed to customers today. Staff can still add a booking here as an override.
            </div>
          )}

          {stations.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center text-slate-500">No active stations. Add stations first.</div>
          ) : (
            <DayGrid stations={stations} blocksByStation={blocksByStation} mode={mode}
              schedule={{ closedDay, openM, closeM }} onRange={onRange} onDelete={deleteBooking}
              onEdit={(b) => { setEditBooking(b); setEditOpen(true); }} />
          )}

          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-slate-400">
            {Object.entries(STATUS_COLOR).map(([k, c]) => (
              <span key={k} className="flex items-center gap-1.5 capitalize"><span className="w-3 h-3 rounded" style={{ background: c }} /> {k}</span>
            ))}
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: BLOCK_COLOR }} /> blocked</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: TOURNAMENT_COLOR }} /> tournament</span>
            <span className="text-slate-500">· {mode === "block" ? "Click or drag to BLOCK a slot" : "Click or drag to create a booking"} (drag across columns for multiple stations)</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="input-cyber pl-10 w-64" placeholder="Search user or station" value={q}
                onChange={(e) => setQ(e.target.value)} data-testid="booking-search" />
            </div>
          </div>
          <TableWrap>
            <thead><Tr><Th>User</Th><Th>Stations</Th><Th>Date & Time</Th><Th>Party</Th><Th>Amount</Th><Th>Points</Th><Th>Status</Th><Th></Th></Tr></thead>
            <tbody>
              {filtered.length === 0 ? <EmptyRow colSpan={8} text="No bookings found" /> : filtered.map((b) => (
                <Tr key={b.id}>
                  <Td>
                    <p className="text-white font-semibold" data-testid={`booking-user-${b.id}`}>{b.user_name || "—"}</p>
                    <p className="text-slate-500 text-xs">{b.user_email}</p>
                    {b.booking_type === "tournament" && (
                      <span className="mt-1 chip inline-flex items-center gap-1.5" style={{ background: "rgba(255,210,63,0.1)", border: "1px solid rgba(255,210,63,0.45)", color: "#ffd23f" }} data-testid={`tournament-chip-${b.id}`}>
                        🏆 Tournament
                      </span>
                    )}
                  </Td>
                  <Td>{b.station_names || "—"}</Td>
                  <Td className="text-slate-300">
                    {b.start_time ? new Date(b.start_time).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz }) : "—"}
                    {" · "}
                    {b.start_time ? new Date(b.start_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: tz }) : ""}
                    {" – "}
                    {b.end_time ? new Date(b.end_time).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: tz }) : ""}
                  </Td>
                  <Td>{b.party_size || 1}</Td>
                  <Td style={{ color: "#00e5ff" }}>${(b.total_amount ?? 0).toFixed(2)}</Td>
                  <Td><span className="inline-flex items-center gap-1 text-xs" style={{ color: "#ffb020" }}><Zap className="w-3 h-3" /> +{b.points_earned || 0} / -{b.points_used || 0}</span></Td>
                  <Td><StatusChip status={b.is_block ? "cancelled" : b.status} /></Td>
                  <Td className="whitespace-nowrap">
                    {b.booking_type === "tournament" ? (
                      <span className="text-slate-500 text-xs italic" data-testid={`tournament-locked-${b.id}`}>Manage via Tournaments</span>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditBooking(b); setEditOpen(true); }}
                          className="text-slate-500 hover:text-cyan-400 mr-3"
                          data-testid={`edit-${b.id}`}
                          title="Edit booking"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteBooking(b.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}

      <ManualBookingModal open={modalOpen} onClose={() => setModalOpen(false)} stations={stations} prefill={prefill} sel={sel} tz={tz} onDone={() => { setModalOpen(false); load(); }} />
      <EditBookingModal
        open={editOpen}
        booking={editBooking}
        stations={stations}
        tz={tz}
        onClose={() => { setEditOpen(false); setEditBooking(null); }}
        onDone={() => { setEditOpen(false); setEditBooking(null); load(); }}
      />
      <BlockModal open={blockOpen} onClose={() => setBlockOpen(false)} stations={stations} prefill={prefill} sel={sel} tz={tz} onDone={() => { setBlockOpen(false); load(); }} />
      <HoursModal open={hoursOpen} onClose={() => setHoursOpen(false)} businessHours={businessHours} onDone={(days) => { setBusinessHours(days); setHoursOpen(false); }} />
      <HolidaysModal open={holidaysOpen} onClose={() => setHolidaysOpen(false)} closures={closures} onChange={(c) => setClosures(c)} />
      <GroupExpiryModal open={groupExpiryOpen} onClose={() => setGroupExpiryOpen(false)} />
      <TaxSettingsModal open={taxOpen} onClose={() => setTaxOpen(false)} />
    </div>
  );
}

const DayGrid = ({ stations, blocksByStation, mode, schedule, onRange, onDelete, onEdit }) => {
  const hourRows = [];
  for (let m = GRID_START; m < GRID_END; m += 60) hourRows.push(m);
  const gridHeight = ROWS * ROW_H;
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);

  useEffect(() => {
    const up = () => {
      const d = dragRef.current;
      if (!d) return;
      const c1 = Math.min(d.anchorCol, d.curCol), c2 = Math.max(d.anchorCol, d.curCol);
      const m1 = Math.min(d.anchorMin, d.curMin), m2 = Math.max(d.anchorMin, d.curMin);
      const stationIds = stations.slice(c1, c2 + 1).map((s) => s.id);
      const endMin = d.moved ? m2 + STEP : m1;
      setDrag(null);
      onRange(stationIds, m1, endMin, d.moved);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [stations, onRange]);

  const minFromEvent = (e, el) => {
    const rect = el.getBoundingClientRect();
    const m = GRID_START + Math.floor((e.clientY - rect.top) / ROW_H) * STEP;
    return Math.max(GRID_START, Math.min(GRID_END - STEP, m));
  };
  const startDrag = (col, e) => {
    if (e.button !== 0) return;
    const m = minFromEvent(e, e.currentTarget);
    setDrag({ anchorCol: col, anchorMin: m, curCol: col, curMin: m, moved: false });
  };
  const moveDrag = (col, e) => {
    if (!dragRef.current) return;
    const m = minFromEvent(e, e.currentTarget);
    setDrag((d) => (d ? { ...d, curCol: col, curMin: m, moved: d.moved || col !== d.anchorCol || m !== d.anchorMin } : d));
  };

  const selRange = drag ? { c1: Math.min(drag.anchorCol, drag.curCol), c2: Math.max(drag.anchorCol, drag.curCol), m1: Math.min(drag.anchorMin, drag.curMin), m2: (drag.moved ? Math.max(drag.anchorMin, drag.curMin) + STEP : drag.anchorMin + STEP) } : null;
  const selColor = mode === "block" ? BLOCK_COLOR : "#00ff9d";

  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid="day-grid">
      <div className="overflow-auto scrollbar-thin" style={{ maxHeight: "68vh" }}>
        <div className="flex min-w-max select-none">
          <div className="sticky left-0 z-20 shrink-0" style={{ width: 64, background: "rgba(6,8,18,0.95)" }}>
            <div className="h-11 border-b border-white/10 sticky top-0 z-10" style={{ background: "rgba(6,8,18,0.98)" }} />
            <div className="relative" style={{ height: gridHeight }}>
              {hourRows.map((m) => (
                <div key={m} className="absolute left-0 right-0 flex items-start justify-end pr-2 text-[10px] text-slate-500 font-mono" style={{ top: ((m - GRID_START) / STEP) * ROW_H - 6 }}>{fmtMin(m)}</div>
              ))}
            </div>
          </div>

          {stations.map((s, colIdx) => (
            <div key={s.id} className="shrink-0 border-l border-white/5" style={{ width: 150 }}>
              <div className="h-11 px-2 flex flex-col justify-center border-b border-white/10 sticky top-0 z-10" style={{ background: "rgba(6,8,18,0.98)" }}>
                <p className="text-white text-xs font-bold truncate flex items-center gap-1"><Gamepad2 className="w-3 h-3 shrink-0" style={{ color: "#00e5ff" }} />{s.name}</p>
                <p className="text-slate-500 text-[10px] capitalize truncate">{s.type?.replace(/_/g, " ")} · ${s.hourly_rate}/hr</p>
              </div>
              <div className="relative" style={{ height: gridHeight, cursor: mode === "block" ? "crosshair" : "cell" }}
                onMouseDown={(e) => startDrag(colIdx, e)} onMouseMove={(e) => moveDrag(colIdx, e)} data-testid={`grid-col-${s.id}`}>
                {/* off-hours / closed shading */}
                {schedule.closedDay ? (
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(45deg, rgba(255,46,151,0.08), rgba(255,46,151,0.08) 8px, transparent 8px, transparent 16px)" }} />
                ) : (
                  <>
                    {schedule.openM > GRID_START && <div className="absolute left-0 right-0 top-0 pointer-events-none" style={{ height: ((Math.min(schedule.openM, GRID_END) - GRID_START) / STEP) * ROW_H, background: "rgba(148,163,184,0.10)" }} />}
                    {schedule.closeM < GRID_END && <div className="absolute left-0 right-0 pointer-events-none" style={{ top: ((Math.max(schedule.closeM, GRID_START) - GRID_START) / STEP) * ROW_H, bottom: 0, background: "rgba(148,163,184,0.10)" }} />}
                  </>
                )}
                {hourRows.map((m) => <div key={m} className="absolute left-0 right-0 border-t border-white/5 pointer-events-none" style={{ top: ((m - GRID_START) / STEP) * ROW_H }} />)}

                {/* drag selection */}
                {selRange && colIdx >= selRange.c1 && colIdx <= selRange.c2 && (
                  <div className="absolute left-0.5 right-0.5 rounded pointer-events-none" style={{ top: ((selRange.m1 - GRID_START) / STEP) * ROW_H, height: Math.max(ROW_H, ((selRange.m2 - selRange.m1) / STEP) * ROW_H), background: `${selColor}33`, border: `1.5px dashed ${selColor}` }} />
                )}

                {(blocksByStation[s.id] || []).map((b) => {
                  const isBlock = b.is_block;
                  const isTournament = b.booking_type === "tournament";
                  const color = isTournament ? TOURNAMENT_COLOR : (isBlock ? BLOCK_COLOR : (STATUS_COLOR[b.status] || "#94a3b8"));
                  const clickable = !isBlock && !isTournament;
                  return (
                    <div key={b.id + s.id} data-testid={`grid-booking-${b.id}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); if (clickable && onEdit) onEdit(b); }}
                      className="absolute left-1 right-1 rounded-md px-1.5 py-0.5 overflow-hidden group"
                      title={isTournament ? "Tournament — manage via Tournaments" : undefined}
                      style={{ top: b._top + 1, height: Math.max(ROW_H - 2, b._height - 2), background: `${color}26`, border: `1px solid ${color}`, cursor: clickable ? "pointer" : "default", ...((isBlock || isTournament) ? { backgroundImage: `repeating-linear-gradient(45deg, ${color}22, ${color}22 5px, transparent 5px, transparent 10px)` } : {}) }}>
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-[10px] font-bold truncate" style={{ color }}>{isTournament ? "🏆 " : (isBlock ? "🚫 " : "")}{isTournament ? (b.tournament_name || "Tournament") : (b.user_name || "Booking")}</p>
                        {clickable && onEdit && (
                          <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100">
                            <button
                              onClick={(e) => { e.stopPropagation(); onEdit(b); }}
                              className="text-slate-400 hover:text-cyan-400"
                              data-testid={`grid-edit-${b.id}`}
                              title="Edit booking"
                            >
                              <Settings className="w-3 h-3" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onDelete(b.id); }} className="text-slate-400 hover:text-red-400" data-testid={`grid-del-${b.id}`}><X className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                      {b._height >= ROW_H * 2 && <p className="text-[9px] text-slate-300 truncate flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{fmtMin(b._startMin)}–{fmtMin(b._endMin)}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StationPicker = ({ stations, selected, onToggle }) => (
  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto scrollbar-thin">
    {stations.map((s) => {
      const on = selected.includes(s.id);
      return (
        <button key={s.id} onClick={() => onToggle(s.id)} data-testid={`pick-station-${s.id}`}
          className="text-left rounded-lg px-3 py-2 text-xs transition-all"
          style={{ background: on ? "rgba(0,229,255,0.12)" : "rgba(8,11,22,0.6)", border: `1px solid ${on ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.14)"}` }}>
          <p className="text-white font-semibold truncate">{s.name}</p>
          <p className="text-slate-500 capitalize truncate">{s.type?.replace(/_/g, " ")}</p>
        </button>
      );
    })}
  </div>
);

const ManualBookingModal = ({ open, onClose, stations, prefill, sel, tz, onDone }) => {
  const [selected, setSelected] = useState([]);
  const [startMin, setStartMin] = useState(18 * 60);
  const [durationMin, setDurationMin] = useState(60);
  const [partySize, setPartySize] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(prefill?.stationIds || []);
      setStartMin(prefill?.startMin ?? 18 * 60);
      setDurationMin(prefill?.durationMin ?? 60);
      setPartySize(1); setName(""); setEmail(""); setNotes("");
    }
  }, [open, prefill]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (selected.length === 0) { toast.error("Select at least one station"); return; }
    if (!name.trim()) { toast.error("Customer name is required"); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("A valid customer email is required"); return; }
    setSaving(true);
    try {
      const start = zonedWallToUtc(sel.y, sel.monthIndex, sel.day, startMin, tz);
      const end = new Date(start.getTime() + durationMin * 60000);
      await apiClient.post("/admin/bookings/manual", {
        station_ids: selected, start_time: start.toISOString(), end_time: end.toISOString(),
        party_size: partySize, customer_name: name.trim(), customer_email: email.trim(), notes: notes || null,
      });
      toast.success("Booking created");
      onDone();
    } catch (err) { toast.error(getErrorMessage(err, "Could not create booking")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manual Booking" testId="manual-booking-modal">
      <div className="mb-4 text-sm text-slate-400 flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> {new Date(sel.y, sel.monthIndex, sel.day).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {tzLabel(tz)}</div>
      <Field label="Stations"><StationPicker stations={stations} selected={selected} onToggle={toggle} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start Time"><select className="input-cyber" value={startMin} onChange={(e) => setStartMin(Number(e.target.value))} data-testid="manual-start">{START_OPTIONS.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtMin(m)}</option>)}</select></Field>
        <Field label="Duration"><select className="input-cyber" value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} data-testid="manual-duration">{DURATION_OPTIONS.map((d) => <option key={d} value={d} className="bg-slate-900">{fmtDur(d)}</option>)}</select></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Customer Name *"><input className="input-cyber" placeholder="John Racer" value={name} onChange={(e) => setName(e.target.value)} data-testid="manual-customer" /></Field>
        <Field label="Customer Email *"><input type="email" className="input-cyber" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="manual-email" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Party Size"><input type="number" min={1} max={20} className="input-cyber" value={partySize} onChange={(e) => setPartySize(Math.max(1, Number(e.target.value)))} data-testid="manual-party" /></Field>
        <Field label="Notes (optional)"><input className="input-cyber" placeholder="Any notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="manual-notes" /></Field>
      </div>
      <div className="text-xs text-slate-500 mb-4">Ends at {fmtMin(startMin + durationMin)}</div>
      <div className="flex gap-3">
        <button onClick={onClose} className="neon-outline-btn flex-1 py-3" data-testid="manual-cancel">Cancel</button>
        <button onClick={submit} disabled={saving} className="neon-btn flex-1 py-3" data-testid="manual-submit">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : "Create Booking"}</button>
      </div>
    </Modal>
  );
};

const BlockModal = ({ open, onClose, stations, prefill, sel, tz, onDone }) => {
  const [selected, setSelected] = useState([]);
  const [startMin, setStartMin] = useState(18 * 60);
  const [durationMin, setDurationMin] = useState(60);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(prefill?.stationIds || []);
      setStartMin(prefill?.startMin ?? 18 * 60);
      setDurationMin(prefill?.durationMin ?? 60);
      setReason("");
    }
  }, [open, prefill]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (selected.length === 0) { toast.error("Select at least one station"); return; }
    if (!reason.trim()) { toast.error("Please add a reason for the block"); return; }
    setSaving(true);
    try {
      const start = zonedWallToUtc(sel.y, sel.monthIndex, sel.day, startMin, tz);
      const end = new Date(start.getTime() + durationMin * 60000);
      await apiClient.post("/admin/bookings/block", { station_ids: selected, start_time: start.toISOString(), end_time: end.toISOString(), reason: reason.trim() });
      toast.success("Slot blocked");
      onDone();
    } catch (err) { toast.error(getErrorMessage(err, "Could not block slot")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Block a Time Slot" testId="block-modal">
      <div className="mb-4 text-sm text-slate-400 flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> {new Date(sel.y, sel.monthIndex, sel.day).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {tzLabel(tz)}</div>
      <Field label="Stations to block"><StationPicker stations={stations} selected={selected} onToggle={toggle} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start Time"><select className="input-cyber" value={startMin} onChange={(e) => setStartMin(Number(e.target.value))} data-testid="block-start">{START_OPTIONS.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtMin(m)}</option>)}</select></Field>
        <Field label="Duration"><select className="input-cyber" value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} data-testid="block-duration">{DURATION_OPTIONS.map((d) => <option key={d} value={d} className="bg-slate-900">{fmtDur(d)}</option>)}</select></Field>
      </div>
      <Field label="Reason * (required)"><input className="input-cyber" placeholder="e.g. Maintenance, private event, staff training" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="block-reason" /></Field>
      <div className="text-xs text-slate-500 mb-4">Ends at {fmtMin(startMin + durationMin)}</div>
      <div className="flex gap-3">
        <button onClick={onClose} className="neon-outline-btn flex-1 py-3" data-testid="block-cancel">Cancel</button>
        <button onClick={submit} disabled={saving} className="flex-1 py-3 rounded-xl font-bold text-black flex items-center justify-center gap-2" style={{ background: BLOCK_COLOR }} data-testid="block-submit">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Blocking...</> : <><Ban className="w-4 h-4" /> Block Slot</>}</button>
      </div>
    </Modal>
  );
};

const HoursModal = ({ open, onClose, businessHours, onDone }) => {
  const [days, setDays] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const base = businessHours && businessHours.length ? businessHours : WEEKDAYS.map((_, i) => ({ weekday: i, is_closed: false, open_minutes: 540, close_minutes: 1320 }));
      setDays(base.map((d) => ({ ...d })));
    }
  }, [open, businessHours]);

  const upd = (i, patch) => setDays((p) => p.map((d) => (d.weekday === i ? { ...d, ...patch } : d)));

  const save = async () => {
    setSaving(true);
    try { const { data } = await apiClient.put("/admin/business-hours", { days }); toast.success("Hours saved"); onDone(data.days); }
    catch (err) { toast.error(getErrorMessage(err, "Could not save hours")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Standard Weekly Hours" testId="hours-modal">
      <p className="text-slate-400 text-sm mb-4">Times are in Atlantic (Halifax). Customers can&apos;t book outside these hours; check &quot;Closed&quot; to close the whole day.</p>
      <div className="space-y-2">
        {days.map((d) => (
          <div key={d.weekday} className="flex items-center gap-2 py-1" data-testid={`hours-row-${d.weekday}`}>
            <span className="w-24 text-sm text-slate-200 shrink-0">{WEEKDAYS[d.weekday]}</span>
            <label className="flex items-center gap-1.5 text-xs text-slate-400 shrink-0 cursor-pointer">
              <input type="checkbox" checked={!!d.is_closed} onChange={(e) => upd(d.weekday, { is_closed: e.target.checked })} data-testid={`hours-closed-${d.weekday}`} /> Closed
            </label>
            <select className="input-cyber !py-1.5 flex-1" value={d.open_minutes} disabled={d.is_closed} onChange={(e) => upd(d.weekday, { open_minutes: Number(e.target.value) })} data-testid={`hours-open-${d.weekday}`}>
              {HOUR_CHOICES.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtMin(m)}</option>)}
            </select>
            <span className="text-slate-500 text-xs">to</span>
            <select className="input-cyber !py-1.5 flex-1" value={d.close_minutes} disabled={d.is_closed} onChange={(e) => upd(d.weekday, { close_minutes: Number(e.target.value) })} data-testid={`hours-close-${d.weekday}`}>
              {HOUR_CHOICES.map((m) => <option key={m} value={m} className="bg-slate-900">{fmtMin(m)}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={onClose} className="neon-outline-btn flex-1 py-3">Cancel</button>
        <button onClick={save} disabled={saving} className="neon-btn flex-1 py-3" data-testid="hours-save">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save Hours"}</button>
      </div>
    </Modal>
  );
};

const HolidaysModal = ({ open, onClose, closures, onChange }) => {
  const [name, setName] = useState("");
  const [date, setDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a holiday name"); return; }
    if (!date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      await apiClient.post("/admin/closures", { name: name.trim(), date: format(date, "yyyy-MM-dd") });
      const { data } = await apiClient.get("/closures");
      onChange(data); setName(""); setDate(null);
      toast.success("Holiday added");
    } catch (err) { toast.error(getErrorMessage(err, "Could not add holiday")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await apiClient.delete(`/admin/closures/${id}`); const { data } = await apiClient.get("/closures"); onChange(data); }
    catch (err) { toast.error(getErrorMessage(err, "Could not remove")); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Holiday Closures" testId="holidays-modal">
      <p className="text-slate-400 text-sm mb-4">Add days the lounge is fully closed. Customers can&apos;t book on these dates.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <input className="input-cyber flex-1" placeholder="e.g. Christmas Day" value={name} onChange={(e) => setName(e.target.value)} data-testid="holiday-name" />
        <Popover>
          <PopoverTrigger asChild>
            <button className="input-cyber flex items-center gap-2 !w-auto px-4" data-testid="holiday-date"><CalendarIcon className="w-4 h-4 text-slate-400" /> {date ? format(date, "MMM d, yyyy") : "Pick date"}</button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 glass border-white/10" align="start"><Calendar mode="single" selected={date} onSelect={setDate} initialFocus /></PopoverContent>
        </Popover>
        <button onClick={add} disabled={saving} className="neon-btn px-5 py-3" data-testid="holiday-add">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}</button>
      </div>
      <div className="space-y-2 max-h-56 overflow-y-auto scrollbar-thin">
        {(!closures || closures.length === 0) ? <p className="text-slate-500 text-sm text-center py-6">No holidays added yet.</p> : closures.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-lg px-3 py-2.5" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }} data-testid={`holiday-${c.id}`}>
            <div>
              <p className="text-white text-sm font-semibold">{c.name}</p>
              <p className="text-slate-500 text-xs">{new Date(c.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
            </div>
            <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-400" data-testid={`holiday-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
};

const GroupExpiryModal = ({ open, onClose }) => {
  const [enabled, setEnabled] = useState(true);
  const [hours, setHours] = useState(48);
  const [buffer, setBuffer] = useState(2);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderHours, setReminderHours] = useState(6);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient.get("/admin/group-expiry-settings")
      .then(({ data }) => {
        setEnabled(data.enabled !== false);
        setHours(data.window_hours ?? 48);
        setBuffer(data.buffer_hours ?? 2);
        setReminderEnabled(data.reminder_enabled !== false);
        setReminderHours(data.reminder_hours_before ?? 6);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/group-expiry-settings", {
        enabled, window_hours: Number(hours), buffer_hours: Number(buffer),
        reminder_enabled: reminderEnabled, reminder_hours_before: Number(reminderHours),
      });
      toast.success("Group invite expiry saved");
      onClose();
    } catch (err) { toast.error(getErrorMessage(err, "Could not save")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Group Invite Auto-Expiry" testId="group-expiry-modal">
      <p className="text-slate-400 text-sm mb-4">When a group / split booking isn&apos;t fully paid within this window, the held stations are automatically released and the organizer is notified. Bookings where someone has already paid a share are never auto-released.</p>
      {loading ? <Spinner label="Loading" /> : (
        <>
          <label className="flex items-center justify-between rounded-xl px-4 py-3 mb-4 cursor-pointer" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <span className="text-sm text-slate-200 font-semibold">Auto-expiry enabled</span>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="group-expiry-enabled" className="w-5 h-5 accent-cyan-400" />
          </label>
          <Field label="Release unpaid invites after">
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={168} step={1} value={hours} disabled={!enabled}
                onChange={(e) => setHours(Math.max(1, Math.min(168, Number(e.target.value) || 1)))}
                className="input-cyber !w-28 disabled:opacity-40" data-testid="group-expiry-hours" />
              <span className="text-slate-400 text-sm">hours ({(hours / 24).toFixed(hours % 24 === 0 ? 0 : 1)} day{hours >= 48 ? "s" : ""})</span>
            </div>
          </Field>
          <div className="text-xs text-slate-500 mt-1 mb-4">Min 1 hour · max 168 hours (7 days). Disabling means group invites are held indefinitely until paid or cancelled.</div>

          <Field label="Minimum buffer before the session">
            <div className="flex items-center gap-3">
              <input type="number" min={0} max={168} step={0.25} value={buffer} disabled={!enabled}
                onChange={(e) => setBuffer(Math.max(0, Math.min(168, Number(e.target.value) || 0)))}
                className="input-cyber !w-28 disabled:opacity-40" data-testid="group-expiry-buffer" />
              <span className="text-slate-400 text-sm">hours</span>
            </div>
          </Field>
          <div className="text-xs text-slate-500 mt-1 mb-4">The hold always ends at least this long before the session starts, and the standard window is also shortened by the buffer. The real hold = min(window − buffer, time-until-session − buffer), never less than 1 hour. Accepts decimals (e.g. 0.25, 0.5). Set to 0 to disable the buffer.</div>

          <div className="border-t border-white/10 pt-4">
            <label className="flex items-center justify-between rounded-xl px-4 py-3 mb-4 cursor-pointer" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
              <span className="text-sm text-slate-200 font-semibold">Send a last-chance reminder</span>
              <input type="checkbox" checked={reminderEnabled} disabled={!enabled} onChange={(e) => setReminderEnabled(e.target.checked)} data-testid="group-reminder-enabled" className="w-5 h-5 accent-cyan-400 disabled:opacity-40" />
            </label>
            <Field label="Remind before expiry">
              <div className="flex items-center gap-3">
                <input type="number" min={1} max={168} step={1} value={reminderHours} disabled={!enabled || !reminderEnabled}
                  onChange={(e) => setReminderHours(Math.max(1, Math.min(168, Number(e.target.value) || 1)))}
                  className="input-cyber !w-28 disabled:opacity-40" data-testid="group-reminder-hours" />
                <span className="text-slate-400 text-sm">hours before release</span>
              </div>
            </Field>
            <div className="text-xs text-slate-500 mt-1 mb-4">The organizer (to cover the balance) and any unpaid guests (to pay their share) get one nudge this many hours before the hold is released. Capped to the window if larger.</div>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="neon-outline-btn flex-1 py-3" data-testid="group-expiry-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="neon-btn flex-1 py-3" data-testid="group-expiry-save">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save"}</button>
          </div>
        </>
      )}
    </Modal>
  );
};

const TaxSettingsModal = ({ open, onClose }) => {
  const [enabled, setEnabled] = useState(true);
  const [rate, setRate] = useState(14);
  const [label, setLabel] = useState("HST");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient.get("/admin/tax-settings")
      .then(({ data }) => {
        setEnabled(data.enabled !== false);
        setRate(data.rate_percent ?? 14);
        setLabel(data.label || "HST");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/tax-settings", { enabled, rate_percent: Number(rate), label });
      toast.success("Tax settings saved");
      onClose();
    } catch (err) { toast.error(getErrorMessage(err, "Could not save")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Sales Tax (HST)" testId="tax-settings-modal">
      <p className="text-slate-400 text-sm mb-4">Tax is added on top of listed prices for every non-$0 transaction (bookings, group split shares, guest bookings and memberships).</p>
      {loading ? <Spinner label="Loading" /> : (
        <>
          <label className="flex items-center justify-between rounded-xl px-4 py-3 mb-4 cursor-pointer" style={{ background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)" }}>
            <span className="text-sm text-slate-200 font-semibold">Collect tax</span>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="tax-enabled" className="w-5 h-5 accent-cyan-400" />
          </label>
          <Field label="Tax label">
            <input className="input-cyber" value={label} disabled={!enabled} onChange={(e) => setLabel(e.target.value)} data-testid="tax-label" />
          </Field>
          <Field label="Rate (%)">
            <div className="flex items-center gap-3">
              <input type="number" min={0} max={100} step={0.01} value={rate} disabled={!enabled}
                onChange={(e) => setRate(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                className="input-cyber !w-28 disabled:opacity-40" data-testid="tax-rate" />
              <span className="text-slate-400 text-sm">% (Nova Scotia HST = 14%)</span>
            </div>
          </Field>
          <div className="flex gap-3 mt-4">
            <button onClick={onClose} className="neon-outline-btn flex-1 py-3" data-testid="tax-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="neon-btn flex-1 py-3" data-testid="tax-save">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : "Save"}</button>
          </div>
        </>
      )}
    </Modal>
  );
};


const EditBookingModal = ({ open, booking, stations, tz, onClose, onDone }) => {
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [stationIds, setStationIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking) {
      const s = new Date(booking.start_time);
      const e = new Date(booking.end_time);
      const parts = zonedParts(s, tz);
      setDate(`${parts.y}-${String(parts.monthIndex + 1).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`);
      setStart(s.toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: tz }));
      setEnd(e.toLocaleTimeString([], { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: tz }));
      setStationIds(booking.station_ids || []);
    }
  }, [booking, tz]);

  if (!open || !booking) return null;
  const toggleSt = (id) => setStationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    setSaving(true);
    try {
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      const [y, mo, d] = date.split("-").map(Number);
      const startIso = zonedWallToUtc(y, mo - 1, d, sh * 60 + sm, tz).toISOString();
      const endIso = zonedWallToUtc(y, mo - 1, d, eh * 60 + em, tz).toISOString();
      await apiClient.put(`/admin/bookings/${booking.id}`, {
        station_ids: stationIds,
        start_time: startIso,
        end_time: endIso,
      });
      toast.success("Booking updated");
      onDone();
    } catch (e) { toast.error(getErrorMessage(e, "Update failed")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Booking">
      <div className="space-y-3">
        <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full px-3 py-2 rounded bg-black/50 border border-slate-700 text-slate-100" data-testid="edit-date" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start"><input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2 rounded bg-black/50 border border-slate-700 text-slate-100" data-testid="edit-start" /></Field>
          <Field label="End"><input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2 rounded bg-black/50 border border-slate-700 text-slate-100" data-testid="edit-end" /></Field>
        </div>
        <Field label="Stations">
          <div className="flex flex-wrap gap-2">
            {stations.map((s) => (
              <button
                key={s.id}
                onClick={() => toggleSt(s.id)}
                type="button"
                className={`px-3 py-1.5 rounded border text-sm ${stationIds.includes(s.id) ? "border-cyan-400 text-cyan-300 bg-cyan-400/10" : "border-slate-700 text-slate-300"}`}
                data-testid={`edit-station-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </Field>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="neon-outline-btn flex-1 py-3">Cancel</button>
          <button onClick={save} disabled={saving} className="neon-btn flex-1 py-3" data-testid="edit-save">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
